import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Sparkles, Plus, Pencil, Trash2, Users } from "lucide-react";
import { PreseVisione } from "@/pages/admin/PreseVisione";
import { splitVoce } from "@/components/NovitaVoce";

const VUOTA = { versione: "", titolo: "", testo: "", pubblicata: true };

const NotaForm = ({ form, setForm, onSave, onCancel }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="novita-form">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      <Input placeholder="Versione (es. 2.1.0)" value={form.versione} onChange={(e) => setForm({ ...form, versione: e.target.value })} data-testid="novita-form-versione" />
      <Input className="sm:col-span-2" placeholder="Titolo" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} data-testid="novita-form-titolo" />
    </div>
    <Textarea rows={8} placeholder={"Una novità per riga. Collegamento facoltativo in fondo alla riga: testo | /percorso (es. | /gestione-sinistri)"} value={form.testo} onChange={(e) => setForm({ ...form, testo: e.target.value })} data-testid="novita-form-voci" />
    <div className="flex items-center justify-between">
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <Switch checked={form.pubblicata} onCheckedChange={(v) => setForm({ ...form, pubblicata: v })} data-testid="novita-form-pubblicata" /> Pubblicata (mostrata agli utenti)
      </label>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancel} data-testid="novita-form-annulla">Annulla</Button>
        <Button onClick={onSave} data-testid="novita-form-salva">Salva</Button>
      </div>
    </div>
  </div>
);

export default function NovitaTab() {
  const [note, setNote] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [aperta, setAperta] = useState(null);

  const load = () => api.get("/novita").then(({ data }) => setNote(data)).catch(() => setNote([]));
  useEffect(() => { load(); }, []);

  const salva = async () => {
    const body = { versione: form.versione, titolo: form.titolo, pubblicata: form.pubblicata, voci: form.testo.split("\n") };
    try {
      if (editId) await api.put(`/novita/${editId}`, body); else await api.post("/novita", body);
      toast.success("Nota salvata"); setForm(null); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || "Errore nel salvataggio"); }
  };

  const elimina = async (n) => {
    if (!window.confirm(`Eliminare la nota della versione ${n.versione}?`)) return;
    await api.delete(`/novita/${n.id}`); load();
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm space-y-4" data-testid="novita-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-lg text-slate-900 flex items-center gap-2"><Sparkles className="h-5 w-5 text-brand-orange" /> Novità di versione</h3>
          <p className="text-sm text-slate-500">Mostrate all'accesso: ogni utente clicca OK per presa visione.</p>
        </div>
        {!form && <Button onClick={() => { setForm(VUOTA); setEditId(null); }} data-testid="novita-nuova-btn"><Plus className="h-4 w-4 mr-1" /> Nuova nota</Button>}
      </div>
      {form && <NotaForm form={form} setForm={setForm} onSave={salva} onCancel={() => { setForm(null); setEditId(null); }} />}
      {note.map((n) => (
        <div key={n.id} className="rounded-lg border border-slate-200 p-3" data-testid={`novita-item-${n.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-medium text-slate-900">Versione {n.versione} · Aggiornamento n. {n.rev ?? "—"} {n.pubblicata ? "" : "(bozza)"}</p>
              <p className="text-sm text-slate-500">{n.titolo}</p>
            </div>
            <div className="flex gap-1">
              <Button size="icon" variant="ghost" onClick={() => { setEditId(n.id); setForm({ versione: n.versione, titolo: n.titolo || "", testo: n.voci.join("\n"), pubblicata: n.pubblicata }); }} data-testid={`novita-modifica-${n.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => elimina(n)} data-testid={`novita-elimina-${n.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          </div>
          <ul className="mt-2 list-disc pl-5 text-sm text-slate-600 space-y-1">{n.voci.map((v, i) => { const { testo, link } = splitVoce(v); return <li key={i}>{testo}{link && <span className="ml-2 font-mono text-xs text-brand-blue">→ {link}</span>}</li>; })}</ul>
          {n.pubblicata && (
            <Button variant="link" className="px-0 h-auto mt-2 text-sm" onClick={() => setAperta(aperta === n.id ? null : n.id)} data-testid={`novita-prese-btn-${n.id}`}>
              <Users className="h-4 w-4 mr-1" /> {aperta === n.id ? "Nascondi prese visione" : "Prese visione"}
            </Button>
          )}
          {aperta === n.id && <PreseVisione noteId={n.id} />}
        </div>
      ))}
    </div>
  );
}
